import { Clock, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { WA_DISPLAY, waLink } from "@/lib/api";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-bronze">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
);

export default function Contact() {
  const { t } = useLanguage();
  usePageTitle(t.meta.contact);

  const info = [
    {
      icon: <Phone className="h-5 w-5 text-bronze" />,
      label: t.contact.phoneLabel,
      value: WA_DISPLAY,
      href: `tel:+4${WA_DISPLAY.replace(/\s/g, "")}`,
      testid: "contact-phone-link",
    },
    {
      icon: <WhatsAppIcon />,
      label: t.contact.whatsappLabel,
      value: t.contact.whatsappText,
      href: waLink(t.common.waMessage),
      testid: "contact-whatsapp-link",
    },
    {
      icon: <MapPin className="h-5 w-5 text-bronze" />,
      label: t.contact.locationLabel,
      value: t.contact.locationValue,
      testid: "contact-location-info",
    },
    {
      icon: <MapPin className="h-5 w-5 text-bronze" />,
      label: t.contact.officeLabel,
      value: t.contact.officeValue,
      testid: "contact-office-info",
    },
    {
      icon: <Clock className="h-5 w-5 text-bronze" />,
      label: t.contact.responseLabel,
      value: t.contact.responseValue,
      testid: "contact-response-info",
    },
    {
      icon: <MapPin className="h-5 w-5 text-bronze" />,
      label: t.contact.areaLabel,
      value: t.contact.areaValue,
      testid: "contact-area-info",
    },
  ];

  return (
    <div data-testid="contact-page" className="pt-24">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-10 md:pt-24">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
              <span className="h-px w-10 bg-bronze" />
              {t.contact.overline}
            </p>
            <h1 className="mt-8 max-w-xl font-serif text-5xl leading-[0.95] tracking-tight text-ink md:text-6xl lg:text-7xl">
              {t.contact.heading}
            </h1>
            <p className="mt-8 max-w-md text-base leading-relaxed text-stone md:text-lg">{t.contact.sub}</p>

            <div className="mt-14 space-y-8">
              {info.map((item) => {
                const inner = (
                  <>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-bronze/30 bg-sand">
                      {item.icon}
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-stone/60">{item.label}</p>
                      <p className="mt-1 text-base text-ink md:text-lg">{item.value}</p>
                    </div>
                  </>
                );
                return item.href ? (
                  <a
                    key={item.testid}
                    data-testid={item.testid}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="group flex items-center gap-5"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={item.testid} data-testid={item.testid} className="flex items-center gap-5">
                    {inner}
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="border border-line bg-white p-7 shadow-2xl shadow-black/40 md:p-10">
              <h2 className="sr-only">{t.contact.formTitle}</h2>
              <LeadForm withMessage source="contact-page" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
